/**
 * TrendChart - Small SVG line chart for registration and assignment trends.
 *
 * Shows:
 * - Curve 1: Cumulative helper registrations over time (step)
 * - Curve 2: Zuordnungen — Gesamt: straight to first day then steps; Fachbereich (linear ramp): straight 0 → today (not race day)
 * - Curve 3: Ziel — linear to target height one week before race day, then flat to race day
 *
 * X-axis: time from first helper creation (left) to race day (right)
 * Mobile-friendly, compact design.
 */
import React, { useMemo } from 'react';
import './TrendChart.css';

export interface TrendPoint {
  date: string;
  cumulative: number;
}

export interface TrendChartData {
  startDate: string;
  endDate: string;
  target: number;
  registrations: TrendPoint[];
  assignments: TrendPoint[];
  assignmentsAvailable?: boolean;
}

type TrendChartSize = 'compact' | 'fb' | 'overall';

interface TrendChartProps {
  data: TrendChartData;
  title?: string;
  compact?: boolean;
  size?: TrendChartSize;
  /** Fachbereich charts: straight line from 0 to current total (misleading DB timestamps). Omit on Gesamt. */
  assignmentsLinearRamp?: boolean;
}

const PAD = { top: 12, right: 12, bottom: 32, left: 36 };
const PAD_COMPACT = { top: 8, right: 8, bottom: 28, left: 28 };

function parseDate(s: string): number {
  if (!s || typeof s !== 'string') return 0;
  const str = String(s).trim();
  const datePart = str.slice(0, 10);
  if (datePart.length < 10) return 0;
  const d = new Date(datePart + 'T12:00:00');
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

function formatDateDe(s: string): string {
  if (!s || typeof s !== 'string') return '';
  const str = s.trim();
  if (str.length < 10) return str;
  const d = new Date(str.includes('T') ? str : str + 'T12:00:00');
  if (isNaN(d.getTime())) return str;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function toStepPath(
  pts: { x: number; y: number }[],
  pad: { top: number; left: number },
  xScale: (v: number) => number,
  yScale: (v: number) => number
): string {
  if (pts.length < 2) return '';
  let d = `M ${pad.left + xScale(pts[0].x)} ${pad.top + yScale(pts[0].y)}`;
  for (let i = 1; i < pts.length; i++) {
    d += ` L ${pad.left + xScale(pts[i].x)} ${pad.top + yScale(pts[i - 1].y)}`;
    d += ` L ${pad.left + xScale(pts[i].x)} ${pad.top + yScale(pts[i].y)}`;
  }
  return d;
}

/** Gesamt Zuordnungen: diagonal from origin to first bucket, then steps. */
function toFirstLinearThenStepPath(
  pts: { x: number; y: number }[],
  pad: { top: number; left: number },
  xScale: (v: number) => number,
  yScale: (v: number) => number
): string {
  if (pts.length < 2) return '';
  let d = `M ${pad.left + xScale(pts[0].x)} ${pad.top + yScale(pts[0].y)}`;
  d += ` L ${pad.left + xScale(pts[1].x)} ${pad.top + yScale(pts[1].y)}`;
  for (let i = 2; i < pts.length; i++) {
    d += ` L ${pad.left + xScale(pts[i].x)} ${pad.top + yScale(pts[i - 1].y)}`;
    d += ` L ${pad.left + xScale(pts[i].x)} ${pad.top + yScale(pts[i].y)}`;
  }
  return d;
}

export const TrendChart: React.FC<TrendChartProps> = ({
  data,
  title,
  compact,
  size,
  assignmentsLinearRamp = false
}) => {
  const { width, height, pad } = useMemo(() => {
    const isCompact = compact || size === 'compact';
    const isOverall = size === 'overall';
    const p = isCompact ? PAD_COMPACT : PAD;
    let w = 320;
    let h = 100;
    if (isOverall) {
      w = 640;
      h = 400;
    } else if (isCompact) {
      w = 320;
      h = 93;
    } else {
      w = 320;
      h = 180;
    }
    return { width: w, height: h, pad: p };
  }, [compact, size]);

  const chartWidth = width - pad.left - pad.right;
  const chartHeight = height - pad.top - pad.bottom;

  const isOverall = size === 'overall';

  const { targetPath, xScale, yScale, todayX, yTicks, regPath, assignPath } = useMemo(() => {
    const startTs = parseDate(data.startDate);
    const endTs = parseDate(data.endDate);
    const range = Math.max(1, endTs - startTs);

    const allReg = data.registrations || [];
    const allAssign = data.assignmentsAvailable !== false ? data.assignments || [] : [];

    const toX = (t: number) => Math.max(0, Math.min(1, (t - startTs) / range));

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const todayTs = parseDate(todayStr);
    const todayXInner = todayTs > startTs && todayTs < endTs ? toX(todayTs) : -1;

    const regPoints: { x: number; y: number }[] = [];
    allReg.forEach((p) => {
      const t = parseDate(p.date);
      if (t >= startTs && t <= endTs && t <= todayTs) {
        regPoints.push({ x: toX(t), y: p.cumulative });
      }
    });
    if (regPoints.length > 0) {
      regPoints.sort((a, b) => a.x - b.x);
      if (regPoints[0].x > 0) regPoints.unshift({ x: 0, y: 0 });
      const lastY = regPoints[regPoints.length - 1].y;
      const lastX = regPoints[regPoints.length - 1].x;
      if (todayXInner >= 0 && lastX < todayXInner) regPoints.push({ x: todayXInner, y: lastY });
    }

    let totalAssign = 0;
    allAssign.forEach((p) => {
      const t = parseDate(p.date);
      if (t >= startTs && t <= endTs && t <= todayTs) {
        totalAssign = Math.max(totalAssign, p.cumulative);
      }
    });

    const assignPoints: { x: number; y: number }[] = [];
    allAssign.forEach((p) => {
      const t = parseDate(p.date);
      if (t >= startTs && t <= endTs && t <= todayTs) {
        assignPoints.push({ x: toX(t), y: p.cumulative });
      }
    });
    if (assignPoints.length > 0) {
      assignPoints.sort((a, b) => a.x - b.x);
      if (assignPoints[0].x > 0) assignPoints.unshift({ x: 0, y: 0 });
      const lastY = assignPoints[assignPoints.length - 1].y;
      const lastX = assignPoints[assignPoints.length - 1].x;
      if (todayXInner >= 0 && lastX < todayXInner) assignPoints.push({ x: todayXInner, y: lastY });
    }

    const maxReg = regPoints.length ? Math.max(...regPoints.map((p) => p.y)) : 0;
    const maxAssignFromSteps = assignPoints.length ? Math.max(...assignPoints.map((p) => p.y)) : 0;
    const maxAssign =
      assignmentsLinearRamp && !isOverall ? totalAssign : maxAssignFromSteps;
    const yMax =
      data.target > 0
        ? Math.max(maxReg, maxAssign, data.target, 1)
        : Math.max(maxReg, maxAssign, 50, 1);

    const yScaleInner = (v: number) => (1 - v / yMax) * chartHeight;
    const xScaleInner = (v: number) => v * chartWidth;

    const regPathInner = toStepPath(regPoints, pad, xScaleInner, yScaleInner);

    // End of linear Zuordnungen ramp: at "Heute" on the axis, not at race day (right edge)
    const linearRampEndX =
      todayXInner >= 0 ? todayXInner : todayTs >= endTs ? 1 : 0;

    let assignPathInner = '';
    if (data.assignmentsAvailable !== false) {
      if (assignmentsLinearRamp && !isOverall && totalAssign > 0) {
        assignPathInner = `M ${pad.left + xScaleInner(0)} ${pad.top + yScaleInner(0)} L ${pad.left + xScaleInner(linearRampEndX)} ${pad.top + yScaleInner(totalAssign)}`;
      } else if (isOverall && assignPoints.length >= 2) {
        assignPathInner = toFirstLinearThenStepPath(assignPoints, pad, xScaleInner, yScaleInner);
      } else if (!isOverall && assignPoints.length >= 2 && !assignmentsLinearRamp) {
        assignPathInner = toStepPath(assignPoints, pad, xScaleInner, yScaleInner);
      }
    }

    const MS_PER_DAY = 86400000;
    const weekBeforeRaceTs = endTs - 7 * MS_PER_DAY;
    const targetReachX = toX(weekBeforeRaceTs);

    let targetPathInner = '';
    if (data.target > 0) {
      targetPathInner = `M ${pad.left + xScaleInner(0)} ${pad.top + yScaleInner(0)} L ${pad.left + xScaleInner(targetReachX)} ${pad.top + yScaleInner(data.target)} L ${pad.left + xScaleInner(1)} ${pad.top + yScaleInner(data.target)}`;
    }

    const yTicks = [0, Math.ceil(yMax / 4), Math.ceil(yMax / 2), Math.ceil((3 * yMax) / 4), yMax].filter(
      (v, i, a) => a.indexOf(v) === i
    );

    return {
      targetPath: targetPathInner,
      xScale: xScaleInner,
      yScale: yScaleInner,
      todayX: todayXInner,
      yMax,
      yTicks,
      regPath: regPathInner,
      assignPath: assignPathInner
    };
  }, [data, chartWidth, chartHeight, pad, assignmentsLinearRamp, isOverall]);

  const sizeClass = size ? `trend-chart-${size}` : compact ? 'trend-chart-compact' : '';
  return (
    <div className={`trend-chart ${sizeClass}`.trim()} aria-label={title || 'Trend-Diagramm'}>
      {title && <div className="trend-chart-title">{title}</div>}
      <div className="trend-chart-svg-wrapper" style={{ aspectRatio: `${width} / ${height}` }}>
        <svg
          className="trend-chart-svg"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
        >
          {targetPath && (
            <path
              d={targetPath}
              className="trend-chart-line trend-chart-target"
              fill="none"
              strokeWidth={1.5}
            />
          )}
          <line
            x1={pad.left}
            y1={pad.top + chartHeight}
            x2={pad.left + chartWidth}
            y2={pad.top + chartHeight}
            className="trend-chart-axis-line"
          />
          {[0, 0.25, 0.5, 0.75, 1].map((x) => (
            <line
              key={x}
              x1={pad.left + xScale(x)}
              y1={pad.top + chartHeight}
              x2={pad.left + xScale(x)}
              y2={pad.top + chartHeight + 4}
              className="trend-chart-axis-tick"
            />
          ))}
          <line x1={pad.left} y1={pad.top} x2={pad.left} y2={pad.top + chartHeight} className="trend-chart-axis-line" />
          {yTicks.map((v) => (
            <g key={v}>
              <line
                x1={pad.left}
                y1={pad.top + yScale(v)}
                x2={pad.left - 4}
                y2={pad.top + yScale(v)}
                className="trend-chart-axis-tick"
              />
              <text
                x={pad.left - 6}
                y={pad.top + yScale(v)}
                className="trend-chart-axis-label"
                textAnchor="end"
                dominantBaseline="middle"
              >
                {v}
              </text>
            </g>
          ))}
          {regPath && (
            <path d={regPath} className="trend-chart-line trend-chart-registrations" fill="none" strokeWidth={2} />
          )}
          {assignPath && (
            <path d={assignPath} className="trend-chart-line trend-chart-assignments" fill="none" strokeWidth={2} />
          )}
          <text x={pad.left} y={height - 4} className="trend-chart-axis-label" textAnchor="start">
            {formatDateDe(data.startDate)}
          </text>
          {todayX >= 0 && todayX < 1 && (
            <>
              <line
                x1={pad.left + xScale(todayX)}
                y1={pad.top}
                x2={pad.left + xScale(todayX)}
                y2={pad.top + chartHeight}
                className="trend-chart-today-line"
              />
              <text
                x={pad.left + xScale(todayX)}
                y={pad.top + chartHeight - 4}
                className="trend-chart-today-label"
                textAnchor="middle"
                dominantBaseline="auto"
                transform={`rotate(-90, ${pad.left + xScale(todayX)}, ${pad.top + chartHeight - 4})`}
              >
                Heute
              </text>
            </>
          )}
          <text x={pad.left + chartWidth} y={height - 4} className="trend-chart-axis-label" textAnchor="end">
            {formatDateDe(data.endDate)}
          </text>
        </svg>
      </div>
      <div className="trend-chart-legend">
        <span className="trend-chart-legend-dot trend-chart-legend-reg" />
        <span>Registrierungen</span>
        <span className="trend-chart-legend-dot trend-chart-legend-assign" />
        <span>Zuordnungen</span>
        {data.target > 0 && (
          <>
            <span className="trend-chart-legend-dot trend-chart-legend-target" />
            <span>Ziel</span>
          </>
        )}
      </div>
    </div>
  );
};

export default TrendChart;
